import React, { useState, useRef, useEffect } from 'react';
import { Card, Button, Space, Tag, Typography, Switch, Input, message } from 'antd';
import { 
  PlayCircleOutlined, 
  PauseCircleOutlined, 
  ClearOutlined, 
  DownloadOutlined,
  SearchOutlined,
  WifiOutlined,
  DisconnectOutlined
} from '@ant-design/icons';
import { useLogService } from '../hooks/useLogService';

const { Text, Paragraph } = Typography;
const { Search } = Input;

const LogViewer = ({ serverUrl = 'ws://localhost:3001', height = 400 }) => {
  const {
    isConnected,
    logs,
    connectionStatus,
    connect,
    disconnect,
    clearLogs,
    sendLog
  } = useLogService(serverUrl, true);

  const [autoScroll, setAutoScroll] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [isPaused, setIsPaused] = useState(false);
  const logContainerRef = useRef(null);

  // 自动滚动到底部
  useEffect(() => {
    if (autoScroll && logContainerRef.current && !isPaused) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs, autoScroll, isPaused]);

  // 过滤日志
  const filteredLogs = logs.filter(log => 
    !filterText || log.formattedLog.toLowerCase().includes(filterText.toLowerCase())
  );

  // 处理连接切换
  const handleConnectionToggle = () => {
    if (isConnected) {
      disconnect();
      message.info('Disconnected from log service');
    } else {
      connect();
      message.info('Connecting to log service...');
    }
  };

  // 导出日志
  const exportLogs = () => {
    if (logs.length === 0) {
      message.warning('No logs to export');
      return;
    }

    const logContent = logs.map(log => log.formattedLog).join('\n');
    const blob = new Blob([logContent], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `logs_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    message.success('Logs exported');
  };

  // 发送测试日志
  const sendTestLog = () => {
    const testMessage = `Frontend local test message - ${new Date().toLocaleString('en-US')}`;
    sendLog(testMessage, 'info');
    message.success('Local test log generated');
  };

  // 获取连接状态显示
  const getConnectionStatus = () => {
    if (isConnected) {
      return (
        <Tag color="green" icon={<WifiOutlined />}>
          Connected
        </Tag>
      );
    } else {
      return (
        <Tag color="red" icon={<DisconnectOutlined />}>
          Disconnected ({connectionStatus.reconnectAttempts}/{connectionStatus.maxReconnectAttempts})
        </Tag>
      );
    }
  };

  return (
    <Card
      title={
        <Space>
          <span>Remote Log Service</span>
          {getConnectionStatus()}
          <Text type="secondary">({filteredLogs.length} logs)</Text>
        </Space>
      }
      extra={
        <Space>
          <Search
            placeholder="Search logs..."
            allowClear
            size="small"
            style={{ width: 200 }}
            onChange={(e) => setFilterText(e.target.value)}
            prefix={<SearchOutlined />}
          />
          <Button
            size="small"
            icon={isPaused ? <PlayCircleOutlined /> : <PauseCircleOutlined />}
            onClick={() => setIsPaused(!isPaused)}
          >
            {isPaused ? 'Resume' : 'Pause'}
          </Button>
          <Button
            size="small"
            icon={<ClearOutlined />}
            onClick={clearLogs}
            disabled={logs.length === 0}
          >
            Clear
          </Button>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            onClick={exportLogs}
            disabled={logs.length === 0}
          >
            Export
          </Button>
        </Space>
      }
      bodyStyle={{ padding: 0 }}
    >
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
        <Space>
          <Button
            type={isConnected ? 'default' : 'primary'}
            onClick={handleConnectionToggle}
            icon={isConnected ? <DisconnectOutlined /> : <WifiOutlined />}
          >
            {isConnected ? 'Disconnect' : 'Connect'}
          </Button>
          <Button onClick={sendTestLog}>
            Generate Test Log
          </Button>
          <div>
            <Text type="secondary">Auto-scroll: </Text>
            <Switch
              size="small"
              checked={autoScroll}
              onChange={setAutoScroll}
            />
          </div>
          <Text type="secondary">Server: {serverUrl}</Text>
        </Space>
      </div>

      <div
        ref={logContainerRef}
        style={{
          height,
          overflow: 'auto',
          backgroundColor: '#001529',
          color: '#fff',
          fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
          fontSize: '12px',
          lineHeight: '18px',
          padding: '8px 12px'
        }}
      >
        {filteredLogs.length === 0 ? (
          <div style={{ 
            color: '#8c8c8c', 
            textAlign: 'center', 
            marginTop: '50px',
            fontStyle: 'italic'
          }}>
            {logs.length === 0 ? 'Waiting for remote log messages...' : 'No matching logs'}
          </div>
        ) : (
          filteredLogs.map((log) => (
            <div
              key={log.id}
              style={{
                marginBottom: '2px',
                padding: '2px 0',
                borderLeft: '3px solid #1890ff',
                paddingLeft: '8px',
                wordBreak: 'break-all',
                whiteSpace: 'pre-wrap'
              }}
            >
              {log.formattedLog}
            </div>
          ))
        )}
      </div>

      {isPaused && (
        <div style={{
          position: 'absolute',
          top: '50%',
          right: '20px',
          transform: 'translateY(-50%)',
          backgroundColor: '#fa8c16',
          color: '#fff',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          zIndex: 1
        }}>
          Paused
        </div>
      )}
    </Card>
  );
};

export default LogViewer; 